import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AudioSourceInfo, AudioTrackWithInfo, useUserContext } from '@/UserContext'
import { cardXPad } from './UserCard'
import { cn } from '@/utlis/cn'
import { AudioDeviceMe } from './AudioDevice/AudioDeviceMe'
import AudioInputDeviceSettingsDialog from '@/pages/AudioInputDeviceSettings/AudioInputDeviceSettingsDialog'
import ElectronIPC from '@/services/ElectronIPC'
import MenuButton from '@/UIComponents2/MenuButton/MenuButton'
import AudioInputsMenu from '../../shared/AudioInputsMenu'
import { Button } from '@/UIComponents2/Button'
import PlusIcon from '@/CustomIcons/PlusIcon'
import { useViewMode } from '../../shared/useViewMode'

export function AudioDevicePanelMe() {
    const { t } = useTranslation()
    const viewMode = useViewMode()
    const myAudioTracks_sorted = useMySortedAudioTracks()
    const [isAudioPermissionGranted, setIsAudioPermissionGranted] = useState<boolean>(true)
    const [openAudioInputDeviceSettingsData, setOpenAudioInputDeviceSettingsData] = useState<AudioSourceInfo | null>(
        null
    )

    useEffect(() => {
        ElectronIPC.getMediaAccessStatus('microphone').then((status) => {
            if (status !== 'granted') {
                setIsAudioPermissionGranted(false)
            }
        })
    }, [])

    return (
        <>
            <div className="flex flex-col gap-4">
                {!isAudioPermissionGranted && (
                    <div className={cn('w-full font-bold size-[14px] text-red-500 pb-4', cardXPad)}>
                        {t('No microphone permission.')}
                    </div>
                )}

                <div>
                    {myAudioTracks_sorted.map((atrack: AudioTrackWithInfo) => {
                        return (
                            <AudioDeviceMe
                                key={atrack.id}
                                atrack={atrack}
                                label={atrack.info.displayLabel}
                                openAudioInputDeviceSettings={(audioSourceInfo) => {
                                    setOpenAudioInputDeviceSettingsData(audioSourceInfo)
                                }}
                            />
                        )
                    })}
                </div>

                <div className="text-center w-full">
                    <MenuButton
                        placement={viewMode.galleryHidden || viewMode.sidebarPoppedOut ? undefined : 'left'}
                        content={<AudioInputsMenu />}
                        customButton={
                            <Button variant="link" className="flex items-center">
                                <PlusIcon />
                                <span className="bg-gradient-to-r from-[#FCE640] to-[#F49629] bg-clip-text text-transparent">
                                    {t('Add Music Device')}
                                </span>
                            </Button>
                        }
                    />
                </div>
            </div>

            {openAudioInputDeviceSettingsData && (
                <AudioInputDeviceSettingsDialog
                    mediaSource={openAudioInputDeviceSettingsData}
                    onClose={() => setOpenAudioInputDeviceSettingsData(null)}
                />
            )}
        </>
    )
}

function useMySortedAudioTracks() {
    const [userState] = useUserContext()
    const myAudioTracks: AudioTrackWithInfo[] = userState.mediaStreams.audioInputs

    const sortTracks = (tracks_: AudioTrackWithInfo[]) => {
        try {
            const tracks = [...tracks_]

            const toSort = tracks.filter((t) => !t.info.isSystemAudio)
            const systemAudios = tracks.filter((t) => t.info.isSystemAudio)

            const orderingLookup = new Map<string, number>()
            userState.mediaSources.audioInputs.forEach((a, i) => orderingLookup.set(a.deviceId, i))

            toSort.sort((a, b) => {
                return (orderingLookup.get(a.info.deviceId) || 9999) - (orderingLookup.get(b.info.deviceId) || 9999)
            })

            systemAudios.sort((a, b) => {
                if (a.info.isMuseAudioShare && !b.info.isMuseAudioShare) {
                    return -1
                }
                return 1
            })

            return [...toSort, ...systemAudios]
        } catch (e) {
            return tracks_
        }
    }

    return sortTracks(myAudioTracks)
}
